import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wand2, ArrowRight, ArrowLeft, Loader2, Sparkles, Clapperboard, Check,
  Gauge, TrendingUp, Clock, DollarSign, Users, Music, Mic, Image as ImageIcon,
  Film, X, AlertCircle, CheckCircle2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CinematicStage, StageHeader, GlassCard, Pill, cx } from "@/components/shared";

const DEFAULT_CONFIG = {
  style: "Cinematic", type: "Story", audience: "General", duration: "60s",
  language: "English", voice: "adult_male", emotion: "Neutral", music: "Cinematic",
  animation: "3D", quality: "1080p", aspect_ratio: "16:9", brand_id: null,
  publish: false, delete_temp: false, cost_tier: "balanced", render_mode: "balanced",
  video_styles: ["Cinematic"], music_styles: ["Cinematic"], emotions: ["Neutral"],
};

function Chips({ options, value, onChange, testid, getLabel = (o) => o, getValue = (o) => o }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const v = getValue(o);
        const active = value === v;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            data-testid={`${testid}-${v}`}
            className={cx(
              "px-4 py-2 rounded-xl text-sm border transition-all duration-200",
              active
                ? "bg-[#5B4DFF]/25 border-[#5B4DFF]/60 text-white glow-purple"
                : "bg-white/5 border-white/10 text-slate-300 hover:border-[#5B4DFF]/40 hover:text-white"
            )}
          >
            {getLabel(o)}
          </button>
        );
      })}
    </div>
  );
}

function MultiChips({ options, values, onToggle, testid, getLabel = (o) => o, getValue = (o) => o }) {
  const sel = Array.isArray(values) ? values : [];
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testid}-multi`}>
      {options.map((o) => {
        const v = getValue(o);
        const active = sel.includes(v);
        return (
          <button
            key={v}
            onClick={() => onToggle(v)}
            aria-pressed={active}
            data-testid={`${testid}-${v}`}
            className={cx(
              "px-4 py-2 rounded-xl text-sm border transition-all duration-200 flex items-center gap-1.5",
              active
                ? "bg-[#5B4DFF]/25 border-[#5B4DFF]/60 text-white glow-purple"
                : "bg-white/5 border-white/10 text-slate-300 hover:border-[#5B4DFF]/40 hover:text-white"
            )}
          >
            {active && <span className="text-[#8B7DFF]">✓</span>}
            {getLabel(o)}
          </button>
        );
      })}
    </div>
  );
}


function ReviewGroup({ label, values, testid }) {
  const vals = Array.isArray(values) ? values.filter(Boolean) : (values ? [values] : []);
  if (vals.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-2">
        {vals.map((v, i) => (
          <span key={`${testid}-${i}`} data-testid={`${testid}-${v}`} className="text-xs px-2.5 py-1 rounded-full bg-[#5B4DFF]/10 border border-[#5B4DFF]/20 text-slate-300">{v}</span>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children, highlight }) {  return (
    <div className={cx(highlight && "rounded-xl -m-2 p-2 ring-1 ring-[#FFB300]/50 bg-[#FFB300]/[0.04]")}>
      <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-2.5 flex items-center gap-1.5">
        {label}
        {highlight && <span className="text-[#FFB300] normal-case tracking-normal text-[10px] font-sans">· required</span>}
      </div>
      {children}
    </div>
  );
}

const REQUIRED_KEYS = ["audience", "duration", "language", "aspect_ratio"];

export default function Create() {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [prompt, setPrompt] = useState(location.state?.prompt || "");
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [catalogs, setCatalogs] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [cost, setCost] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [baseConfig, setBaseConfig] = useState(DEFAULT_CONFIG);
  const fromTemplate = !!location.state?.config;

  const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));
  const toggle = (k, v) => setConfig((c) => {
    const arr = Array.isArray(c[k]) ? c[k] : [];
    return { ...c, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
  });

  const missingRequired = REQUIRED_KEYS.filter((k) => !config[k]);
  const missingSet = new Set(analysis && analysis.completeness !== "complete" ? missingRequired : []);

  const analyzeAndContinue = async () => {
    if (fromTemplate) { setStep(2); return; }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/prompt/analyze", { prompt });
      // Each analysis starts from the neutral base — a new brief never inherits
      // stale settings from a previously analyzed prompt.
      const merged = { ...baseConfig, ...(data.config || {}) };
      // Arrays are the authoritative source of truth. The AI brief analysis now
      // returns MULTIPLE values per creative field; consume the arrays directly
      // and fall back to a legacy single only if no array was provided. Never
      // reduce a returned array to its first element.
      const arr = (a, single) =>
        Array.isArray(a) && a.length ? a : (single ? [single] : undefined);
      const vs = arr(data.config?.video_styles, data.config?.style);
      const ms = arr(data.config?.music_styles, data.config?.music);
      const es = arr(data.config?.emotions, data.config?.emotion);
      if (vs) merged.video_styles = vs;
      if (ms) merged.music_styles = ms;
      if (es) merged.emotions = es;
      if (data.completeness !== "complete") {
        for (const k of data.missing_required || []) merged[k] = null;
      }
      setConfig(merged);
      setAnalysis({ completeness: data.completeness, missing: data.missing_required || [] });
      if (data.completeness === "complete") toast.success("Production configured from your brief");
    } catch (e) {
      setAnalysis(null); // graceful fallback → manual configuration
      toast.message("Configure your production below");
    } finally {
      setAnalyzing(false);
      setStep(2);
    }
  };

  useEffect(() => {
    api.get("/catalogs").then(({ data }) => setCatalogs(data)).catch(() => {});
    if (location.state?.config) setConfig((c) => ({ ...c, ...location.state.config }));
    api.get("/brand").then(({ data }) => {
      const bd = {
        style: location.state?.config?.style || data.preferred_style || DEFAULT_CONFIG.style,
        voice: location.state?.config?.voice || data.preferred_voice || DEFAULT_CONFIG.voice,
        music: location.state?.config?.music || data.preferred_music || DEFAULT_CONFIG.music,
        quality: data.default_quality || DEFAULT_CONFIG.quality,
      };
      // Mirror brand legacy singles into the authoritative arrays.
      bd.video_styles = [bd.style];
      bd.music_styles = [bd.music];
      setConfig((c) => ({ ...c, ...bd }));
      setBaseConfig((b) => ({ ...b, ...bd }));
    }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (step === 3) {
      api.post("/cost/estimate", { prompt, config }).then(({ data }) => setCost(data)).catch(() => {});
      api.post("/cost/tiers", { prompt, config }).then(({ data }) => setTiers(data)).catch(() => {});
    }
  }, [step, config.cost_tier]); // eslint-disable-line

  const generatePlan = async () => {
    setPlanLoading(true);
    try {
      const { data } = await api.post("/director/plan", { prompt, config });
      setPlan(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to plan");
    } finally {
      setPlanLoading(false);
    }
  };

  const approveProduction = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { prompt, config, plan });
      toast.success("Production approved — generation started");
      navigate(`/projects/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to start");
      setCreating(false);
    }
  };

  if (!catalogs)
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#5B4DFF]" /></div>;

  // AI DIRECTOR VIEW
  if (plan) {
    return <DirectorPlan plan={plan} onApprove={approveProduction} onModify={() => { setPlan(null); setStep(2); }} creating={creating} />;
  }

  const durations = catalogs.durations;

  return (
    <CinematicStage>
    <div className="max-w-4xl mx-auto">
      <StageHeader stage="01–03" room="The Director's Table · Create" title="Direct your new film" subtitle="Describe your idea, configure the production, then approve your AI director's plan." />
      {/* Stepper */}
      <div className="flex items-center justify-center gap-2 sm:gap-4 mb-10">
        {["Idea", "Configure", "Review"].map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <div key={label} className="flex items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2">
                <div className={cx(
                  "w-8 h-8 rounded-full grid place-items-center text-sm font-semibold transition",
                  active ? "bg-[#5B4DFF] text-white glow-purple" : done ? "bg-[#FFB300] text-[#081229]" : "bg-white/5 text-slate-500 border border-white/10"
                )}>
                  {done ? <Check className="w-4 h-4" /> : n}
                </div>
                <span className={cx("text-sm hidden sm:block", active ? "text-white" : "text-slate-500")}>{label}</span>
              </div>
              {n < 3 && <div className="w-8 sm:w-16 h-px bg-white/10" />}
            </div>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <GlassCard className="p-8">
              <h2 className="font-display text-2xl font-semibold mb-2">What do you want to create?</h2>
              <p className="text-slate-400 mb-6">Describe your idea in a sentence. The AI director handles the rest.</p>
              <textarea
                data-testid="create-prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={5}
                autoFocus
                placeholder="Create a 10-minute Pixar-style animated documentary about Lord Krishna for children…"
                className="w-full bg-white/5 border border-white/10 focus:border-[#5B4DFF]/60 rounded-2xl p-4 outline-none resize-none text-white placeholder:text-slate-500"
              />
              <div className="flex justify-end mt-6">
                <button
                  disabled={prompt.trim().length < 8 || analyzing}
                  onClick={analyzeAndContinue}
                  data-testid="create-next-step1"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#5B4DFF] hover:bg-[#4a3ff0] text-white font-semibold transition glow-purple disabled:opacity-40"
                >
                  {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Configuring…</> : <>Configure <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </GlassCard>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <GlassCard className="p-8 space-y-7">
              {analysis && analysis.completeness === "complete" && (
                <div data-testid="config-ready-banner" className="flex items-start gap-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3.5">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-200">Your production is configured and ready. Review or adjust anything below, then continue.</div>
                </div>
              )}
              {analysis && analysis.completeness === "partial" && (
                <div data-testid="config-partial-banner" className="flex items-start gap-3 rounded-xl bg-[#FFB300]/10 border border-[#FFB300]/30 p-3.5">
                  <AlertCircle className="w-5 h-5 text-[#FFB300] shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-200">Please complete the highlighted options below before continuing.</div>
                </div>
              )}
              {analysis && analysis.completeness === "idea" && (
                <div data-testid="config-idea-banner" className="flex items-start gap-3 rounded-xl bg-[#FFB300]/10 border border-[#FFB300]/30 p-3.5">
                  <AlertCircle className="w-5 h-5 text-[#FFB300] shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-200">Please configure your production before continuing.</div>
                </div>
              )}
              <Field label="Video Style">
                <MultiChips options={catalogs.video_styles} values={config.video_styles} onToggle={(v) => toggle("video_styles", v)} testid="chip-style" />
              </Field>
              <Field label="Video Type">
                <Chips options={catalogs.video_types} value={config.type} onChange={(v) => set("type", v)} testid="chip-type" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-7">
                <Field label="Audience" highlight={missingSet.has("audience")}>
                  <Chips options={catalogs.audiences} value={config.audience} onChange={(v) => set("audience", v)} testid="chip-audience" />
                </Field>
                <Field label="Duration" highlight={missingSet.has("duration")}>
                  <Chips options={durations} value={config.duration} onChange={(v) => set("duration", v)} testid="chip-duration" getLabel={(o) => o.label} getValue={(o) => o.id} />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <Field label="Language" highlight={missingSet.has("language")}>
                  <Chips options={catalogs.languages} value={config.language} onChange={(v) => set("language", v)} testid="chip-language" />
                </Field>
                <Field label="Voice">
                  <Chips options={catalogs.voices} value={config.voice} onChange={(v) => set("voice", v)} testid="chip-voice" getLabel={(o) => `${o.name} · ${o.type}`} getValue={(o) => o.id} />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <Field label="Emotion">
                  <MultiChips options={catalogs.emotions} values={config.emotions} onToggle={(v) => toggle("emotions", v)} testid="chip-emotion" />
                </Field>
                <Field label="Music Style">
                  <MultiChips options={catalogs.music_styles} values={config.music_styles} onToggle={(v) => toggle("music_styles", v)} testid="chip-music" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-7">
                <Field label="Animation">
                  <Chips options={catalogs.animation_styles} value={config.animation} onChange={(v) => set("animation", v)} testid="chip-animation" />
                </Field>
                <Field label="Quality">
                  <Chips options={catalogs.qualities} value={config.quality} onChange={(v) => set("quality", v)} testid="chip-quality" />
                </Field>
                <Field label="Aspect Ratio" highlight={missingSet.has("aspect_ratio")}>
                  <Chips options={catalogs.aspect_ratios} value={config.aspect_ratio} onChange={(v) => set("aspect_ratio", v)} testid="chip-aspect" getLabel={(o) => o.id} getValue={(o) => o.id} />
                </Field>
              </div>

              <Field label="Render Mode (video quality preset)">
                <Chips options={catalogs.render_modes || []} value={config.render_mode} onChange={(v) => set("render_mode", v)} testid="chip-rendermode" getLabel={(o) => o.label} getValue={(o) => o.id} />
                <div className="text-xs text-slate-500 mt-2">{(catalogs.render_modes || []).find((m) => m.id === config.render_mode)?.desc}</div>
              </Field>

              <div className="flex flex-wrap gap-4 pt-2">
                <Toggle label="Publish after render" checked={config.publish} onChange={(v) => set("publish", v)} testid="toggle-publish" />
                <Toggle label="Delete temp files after download" checked={config.delete_temp} onChange={(v) => set("delete_temp", v)} testid="toggle-delete" />
              </div>

              <div className="flex justify-between pt-2 items-center">
                <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:text-white" data-testid="create-back-step2">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <div className="flex items-center gap-3">
                  {missingRequired.length > 0 && (
                    <span className="text-xs text-[#FFB300]" data-testid="missing-required-hint">Select {missingRequired.length} required option{missingRequired.length > 1 ? "s" : ""}</span>
                  )}
                  <button onClick={() => { setConfig((c) => ({ ...DEFAULT_CONFIG, ...Object.fromEntries(Object.entries(c).filter(([, v]) => v != null)) })); setStep(3); }} disabled={missingRequired.length > 0} data-testid="create-next-step2" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#5B4DFF] hover:bg-[#4a3ff0] text-white font-semibold transition glow-purple disabled:opacity-40">
                    Review <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <GlassCard className="p-8">
              <Pill tone="gold" className="mb-4"><Clapperboard className="w-3 h-3" /> AI Director Mode</Pill>
              <h2 className="font-display text-2xl font-semibold mb-2">Ready for the director's plan</h2>
              <p className="text-slate-400 mb-6">Vyom will act as your film director and prepare a full production plan before spending on generation.</p>

              <div className="rounded-2xl bg-white/5 border border-white/10 p-5 mb-6 space-y-4" data-testid="review-config">
                <div className="text-sm text-slate-300 italic">"{prompt}"</div>
                <ReviewGroup label="Video Style" values={config.video_styles} testid="review-video-styles" />
                <div className="flex flex-wrap gap-2" data-testid="review-single-fields">
                  {[config.type, config.audience, config.duration, config.language, config.voice, config.animation, config.quality, config.aspect_ratio].filter(Boolean).map((t, ti) => (
                    <span key={`rev-${ti}`} className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300">{t}</span>
                  ))}
                </div>
                <ReviewGroup label="Music" values={config.music_styles} testid="review-music-styles" />
                <ReviewGroup label="Emotion" values={config.emotions} testid="review-emotions" />
              </div>

              {tiers && (
                <div className="mb-6">
                  <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-3">Smart Cost Optimizer — choose your tier</div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {tiers.map((t) => {
                      const active = config.cost_tier === t.id;
                      return (
                        <button key={t.id} onClick={() => set("cost_tier", t.id)} data-testid={`cost-tier-${t.id}`}
                          className={cx("text-left p-4 rounded-2xl border transition-all",
                            active ? "bg-[#5B4DFF]/20 border-[#5B4DFF]/60 glow-purple" : "bg-white/5 border-white/10 hover:border-[#5B4DFF]/40")}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white font-semibold text-sm">{t.label}</span>
                            {active && <Check className="w-4 h-4 text-[#FFB300]" />}
                          </div>
                          <div className="font-display text-2xl font-bold text-white">${t.real_total}</div>
                          <div className="text-[11px] text-slate-400 mt-1">{t.desc}</div>
                          <div className="text-[11px] text-slate-500 mt-2 font-mono">{t.quality} · {t.images} imgs · ~{t.estimated_time_seconds}s</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:text-white" data-testid="create-back-step3">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={generatePlan} disabled={planLoading} data-testid="create-generate-plan" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#FFB300] text-[#081229] font-semibold transition glow-gold disabled:opacity-60">
                  {planLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Directing…</> : <><Wand2 className="w-4 h-4" /> Generate Production Plan</>}
                </button>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </CinematicStage>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className="flex items-center gap-3 text-sm text-slate-300"
    >
      <span className={cx("w-11 h-6 rounded-full p-0.5 transition", checked ? "bg-[#5B4DFF]" : "bg-white/10")}>
        <span className={cx("block w-5 h-5 rounded-full bg-white transition-transform", checked && "translate-x-5")} />
      </span>
      {label}
    </button>
  );
}

function ScoreRing({ value, label, icon: Icon, tone = "#5B4DFF" }) {
  return (
    <GlassCard className="p-5 text-center">
      <div className="relative w-20 h-20 mx-auto">
        <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke={tone} strokeWidth="3" strokeLinecap="round"
            strokeDasharray={`${value}, 100`} />
        </svg>
        <div className="absolute inset-0 grid place-items-center">
          <span className="font-display text-xl font-bold">{value}</span>
        </div>
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-slate-400">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
    </GlassCard>
  );
}

function DirectorPlan({ plan, onApprove, onModify, creating }) {
  const cost = plan.cost || {};
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto space-y-6">
      <div className="text-center">
        <Pill tone="gold" className="mb-3"><Clapperboard className="w-3 h-3" /> Production Plan by Vyom</Pill>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-gradient">{plan.suggested_title}</h1>
      </div>

      <GlassCard className="p-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-2">Story Summary</div>
              <p className="text-slate-200 leading-relaxed">{plan.story_summary}</p>
            </div>

            {plan.character_suggestions?.length > 0 && (
              <div>
                <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Characters</div>
                <div className="space-y-2">
                  {plan.character_suggestions.map((c, i) => (
                    <div key={i} className="flex gap-3 items-start rounded-xl bg-white/5 border border-white/10 p-3">
                      <div className="w-8 h-8 rounded-lg bg-[#5B4DFF]/20 grid place-items-center text-sm font-semibold">{(c.name || "?")[0]}</div>
                      <div>
                        <div className="text-white text-sm font-medium">{c.name}</div>
                        <div className="text-slate-400 text-xs">{c.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <Recommendation icon={ImageIcon} label="Animation" value={plan.animation_style_recommendation} />
              <Recommendation icon={Mic} label="Voice" value={plan.voice_recommendation} />
              <Recommendation icon={Music} label="Music" value={plan.music_recommendation} />
              <Recommendation icon={Film} label="Thumbnail" value={plan.thumbnail_concept} />
            </div>

            {plan.creative_notes?.length > 0 && (
              <div>
                <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-2">Director's Notes</div>
                <ul className="space-y-1.5">
                  {plan.creative_notes.map((n, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-300"><Sparkles className="w-4 h-4 text-[#5B4DFF] shrink-0 mt-0.5" /> {n}</li>
                  ))}
                </ul>
              </div>
            )}

            {plan.recommendations?.length > 0 && (
              <div>
                <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-2 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> AI Director recommends</div>
                <div className="space-y-2">
                  {plan.recommendations.map((r, i) => (
                    <div key={i} className="rounded-xl bg-[#5B4DFF]/8 border border-[#5B4DFF]/20 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-white">{r.area}</span>
                        {r.impact && <span className="text-xs font-mono text-emerald-300">{r.impact}</span>}
                      </div>
                      {r.issue && <div className="text-xs text-slate-400 mt-0.5">{r.issue}</div>}
                      {r.fix && <div className="text-xs text-[#b6aaff] mt-1">→ {r.fix}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <ScoreRing value={plan.story_quality_score} label="Quality" icon={Gauge} />
              <ScoreRing value={plan.audience_retention_prediction} label="Retention" icon={TrendingUp} tone="#FFB300" />
            </div>
            <GlassCard className="p-4 space-y-3">
              <Meta icon={Clock} label="Est. Duration" value={plan.estimated_duration} />
              <Meta icon={Film} label="Scenes" value={plan.estimated_scenes} />
              <Meta icon={Clock} label="Gen. Time" value={`~${plan.estimated_generation_time_seconds}s`} />
              <Meta icon={DollarSign} label="Est. Cost" value={`$${cost.real_total ?? "—"}`} tone />
            </GlassCard>
          </div>
        </div>
      </GlassCard>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button onClick={onModify} data-testid="director-modify-button" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white/5 border border-white/10 text-slate-200 hover:text-white transition">
          <X className="w-4 h-4" /> Modify Production
        </button>
        <button onClick={onApprove} disabled={creating} data-testid="director-approve-button" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-[#FFB300] text-[#081229] font-bold transition glow-gold disabled:opacity-60">
          {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</> : <><Check className="w-4 h-4" /> Approve Production</>}
        </button>
      </div>
    </motion.div>
  );
}

function Recommendation({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="text-sm text-white">{value}</div>
    </div>
  );
}

function Meta({ icon: Icon, label, value, tone }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm text-slate-400"><Icon className="w-4 h-4" /> {label}</span>
      <span className={cx("font-semibold", tone ? "text-[#FFB300]" : "text-white")}>{value}</span>
    </div>
  );
}
